import { z } from 'zod';
import { OffsetPaginationParamsSchema } from '../offset-pagination.js';
import { TRIAL_ROLE_CODES } from './trial-roles.js';
// ---------------------------------------------------------------------------
// Enums / catalogs
// ---------------------------------------------------------------------------
/** Protocol visit kinds (legacy Trial_visit.Kind). */
export const TRIAL_VISIT_KINDS = {
    SCREENING: 'SCR',
    RANDOMIZATION: 'RND',
    REGULAR: 'REGULAR',
    END_OF_TREATMENT: 'EOT',
    FOLLOW_UP: 'FV',
    OUT_OF_PROTOCOL: 'OUT',
    UNSCHEDULED: 'UNS',
    OTHER: 'OTH',
};
export const TrialVisitKindSchema = z.enum(Object.values(TRIAL_VISIT_KINDS));
/** Visit type char (legacy Trial_visit.Type): S/P/H/I. */
export const TRIAL_VISIT_TYPES = {
    S: 'S',
    P: 'P',
    H: 'H',
    I: 'I',
};
export const TrialVisitTypeSchema = z.enum(Object.values(TRIAL_VISIT_TYPES));
/**
 * Visit contact type (legacy Trial_visit.Contact).
 * REQ-TRIAL-007: "ośrodek, telefon, dom, ośrodek wyłącznie podanie leku".
 * Codes mirror the legacy values (lowercased) so the DB default 'site' matches.
 * Polish labels (UI): Ośrodek / Telefon / Dom / Ośrodek – wyłącznie podanie leku.
 */
export const TRIAL_VISIT_CONTACTS = {
    SITE: 'site',
    PHONE: 'phone',
    SITE_IMP: 'site_imp',
    HOME: 'home',
};
export const TrialVisitContactSchema = z.enum(Object.values(TRIAL_VISIT_CONTACTS));
/**
 * Window reference semantics (D4, #35): 'visit' = relative to a referral visit,
 * 'date' = ad-hoc concrete date given at scheduling. Not DB-constrained (same
 * as kind/contact_type); the Zod enum is the source of truth.
 */
export const TRIAL_VISIT_REFERENCE_TYPES = {
    VISIT: 'visit',
    DATE: 'date',
};
export const TrialVisitReferenceTypeSchema = z.enum(Object.values(TRIAL_VISIT_REFERENCE_TYPES));
/** CTA kind (legacy Trial.CTA_kind): 2 or 3. */
export const TRIAL_CTA_KINDS = {
    TWO: 2,
    THREE: 3,
};
export const TrialCtaKindSchema = z.union([z.literal(2), z.literal(3)]);
// ---------------------------------------------------------------------------
// Trial card (REQ-001…004, 011, 012, 026)
// ---------------------------------------------------------------------------
export const TrialDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    sponsorId: z.number().int().positive(),
    croId: z.number().int().positive().nullable(),
    protocolNumber: z.string().min(1).max(100),
    shortTitle: z.string().min(1).max(255),
    // Legacy rows may exceed the 10-char configuration limit — the DB column
    // varchar(20) is deliberately NOT narrowed (Piotr #7 2026-11-02 odp.5), so
    // the DTO stays lenient; writes enforce ≤10 (Create/Update below).
    shortcut: z.string().min(1).max(20),
    siteNumber: z.string().min(1).max(50),
    comment: z.string().nullable(),
    randomizationNumber: z.boolean(),
    sivDate: z.iso.date().nullable(),
    covDate: z.iso.date().nullable(),
    active: z.boolean(),
    dayZeroBased: z.boolean(),
    travelCosts: z.boolean(),
    travelCostsInfo: z.string().nullable(),
    labModule: z.boolean(),
    compliance: z.boolean(),
    vendorsInfo: z.string().nullable(),
    ctaKind: TrialCtaKindSchema.nullable(),
    paymentTimelines: z.number().int().nullable(),
    paymentFrequency: z.number().int().positive(),
    invoiceSendDescription: z.string().nullable(),
    withholdingRate: z.number().int().min(0).max(100),
    feeVariants: z.boolean(),
    procedureVariants: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const CreateTrialRequestSchema = z.object({
    sponsorId: z.number().int().positive(),
    croId: z.number().int().positive().optional(),
    protocolNumber: z.string().min(1).max(100),
    shortTitle: z.string().min(1).max(255),
    // ≤10 chars wherever the shortcut is CONFIGURED — trial card and bars
    // window alike (Piotr #7 2026-11-02 odp.5: „Proponuję 10")
    shortcut: z.string().min(1).max(10),
    siteNumber: z.string().min(1).max(50),
    comment: z.string().max(10_000).optional(),
    randomizationNumber: z.boolean().default(false),
    sivDate: z.iso.date().optional(),
    covDate: z.iso.date().optional(),
    active: z.boolean().default(true),
    dayZeroBased: z.boolean().default(false),
    travelCosts: z.boolean().default(false),
    travelCostsInfo: z.string().max(10_000).optional(),
    labModule: z.boolean().default(false),
    compliance: z.boolean().default(false),
    vendorsInfo: z.string().max(10_000).optional(),
    ctaKind: TrialCtaKindSchema.optional(),
    paymentTimelines: z.number().int().positive().optional(),
    paymentFrequency: z.number().int().positive().default(90),
    invoiceSendDescription: z.string().max(10_000).optional(),
    withholdingRate: z.number().int().min(0).max(100).default(0),
    feeVariants: z.boolean().default(false),
    procedureVariants: z.boolean().default(false),
});
export const UpdateTrialRequestSchema = z.object({
    croId: z.number().int().positive().nullable().optional(),
    protocolNumber: z.string().min(1).max(100).optional(),
    shortTitle: z.string().min(1).max(255).optional(),
    shortcut: z.string().min(1).max(10).optional(),
    siteNumber: z.string().min(1).max(50).optional(),
    comment: z.string().max(10_000).nullable().optional(),
    randomizationNumber: z.boolean().optional(),
    sivDate: z.iso.date().nullable().optional(),
    covDate: z.iso.date().nullable().optional(),
    active: z.boolean().optional(),
    dayZeroBased: z.boolean().optional(),
    travelCosts: z.boolean().optional(),
    travelCostsInfo: z.string().max(10_000).nullable().optional(),
    labModule: z.boolean().optional(),
    compliance: z.boolean().optional(),
    vendorsInfo: z.string().max(10_000).nullable().optional(),
    ctaKind: TrialCtaKindSchema.nullable().optional(),
    paymentTimelines: z.number().int().positive().nullable().optional(),
    paymentFrequency: z.number().int().positive().optional(),
    invoiceSendDescription: z.string().max(10_000).nullable().optional(),
    withholdingRate: z.number().int().min(0).max(100).optional(),
    feeVariants: z.boolean().optional(),
    procedureVariants: z.boolean().optional(),
});
export const TrialListQuerySchema = OffsetPaginationParamsSchema.extend({
    // Query params arrive as strings; z.coerce.boolean() would make "false"
    // truthy — use stringbool (Zod 4 query-param helper), boolean allowed too.
    active: z.union([z.boolean(), z.stringbool()]).optional(),
    search: z.string().max(100).optional(),
});
// ---------------------------------------------------------------------------
// Trial visits (REQ-007…010)
// ---------------------------------------------------------------------------
export const TrialVisitDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    visitNumber: z.number().int().positive(),
    visitName1: z.string().min(1).max(255),
    visitName2: z.string().max(255).nullable(),
    kind: TrialVisitKindSchema,
    window: z.boolean(),
    minDays: z.number().int().nullable(),
    optDays: z.number().int().nullable(),
    maxDays: z.number().int().nullable(),
    referralVisitId: z.number().int().positive().nullable(),
    type: TrialVisitTypeSchema,
    contactType: TrialVisitContactSchema,
    referenceType: TrialVisitReferenceTypeSchema,
    referenceInstruction: z.string().nullable(),
    description: z.string().nullable(),
    // D2 (#40): optional visit group (N:1); null = "bez grupy".
    groupId: z.number().int().positive().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Operator-facing labels for the window day fields (#44 atom 1) — contract
 * validation fires BEFORE the service, so these messages must already be in
 * Polish and identical to the service's backstop wording.
 */
const VISIT_WINDOW_FIELD_LABELS_PL = {
    minDays: 'minimum',
    optDays: 'optimum',
    maxDays: 'maksimum',
};
/**
 * #44 round 2 (A1/A2): a window day range must be non-negative and ordered
 * MIN <= OPT <= MAX. Null/undefined counts as "absent", so partial updates are
 * only checked on the provided fields — the service re-checks the final merged
 * state (A3/A4 normalization also lives there).
 */
function visitWindowDayRangeIssues(minDays, optDays, maxDays) {
    const issues = [];
    const fields = [
        ['minDays', minDays],
        ['optDays', optDays],
        ['maxDays', maxDays],
    ];
    for (const [path, value] of fields) {
        if (typeof value === 'number' && value < 0) {
            issues.push({
                path,
                message: `Liczba dni okna (${VISIT_WINDOW_FIELD_LABELS_PL[path]}) nie może być ujemna`,
            });
        }
    }
    if (typeof minDays === 'number' &&
        typeof optDays === 'number' &&
        minDays > optDays) {
        issues.push({
            path: 'minDays',
            message: 'Minimum okna nie może być większe niż optimum',
        });
    }
    if (typeof optDays === 'number' &&
        typeof maxDays === 'number' &&
        optDays > maxDays) {
        issues.push({
            path: 'optDays',
            message: 'Optimum okna nie może być większe niż maksimum',
        });
    }
    return issues;
}
/** `Włączone okno wymaga wartości maksimum i wizyty odniesienia` (REQ-009/010). */
function windowedVisitRequiresMessage(missing) {
    const labels = missing.map((field) => field === 'referralVisitId' ? 'wizyty odniesienia' : 'wartości maksimum');
    return `Włączone okno wymaga ${labels.join(' i ')}`;
}
/**
 * REQ-009/010: when window=true, all day counts AND the referral visit are
 * REQUIRED (refinement mirrors the DB CHECK).
 */
export const CreateTrialVisitRequestSchema = z
    .object({
    trialId: z.number().int().positive(),
    visitName1: z.string().min(1).max(255),
    visitName2: z.string().max(255).optional(),
    kind: TrialVisitKindSchema,
    window: z.boolean().default(false),
    minDays: z.number().int().optional(),
    optDays: z.number().int().optional(),
    maxDays: z.number().int().optional(),
    referralVisitId: z.number().int().positive().optional(),
    type: TrialVisitTypeSchema.default('S'),
    contactType: TrialVisitContactSchema.default('site'),
    referenceType: TrialVisitReferenceTypeSchema.default('visit'),
    referenceInstruction: z.string().max(10_000).optional(),
    description: z.string().max(10_000).optional(),
    // D2 (#40): optional visit group; the service validates that the group
    // belongs to the same tenant+trial.
    groupId: z.number().int().positive().optional(),
})
    .superRefine((val, ctx) => {
    for (const issue of visitWindowDayRangeIssues(val.minDays, val.optDays, val.maxDays)) {
        ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
    }
    if (!val.window)
        return;
    const missing = [];
    if (val.maxDays === undefined)
        missing.push('maxDays');
    if (val.referralVisitId === undefined)
        missing.push('referralVisitId');
    if (missing.length > 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['window'],
            message: windowedVisitRequiresMessage(missing),
        });
    }
});
export const UpdateTrialVisitRequestSchema = z
    .object({
    visitName1: z.string().min(1).max(255).optional(),
    visitName2: z.string().max(255).nullable().optional(),
    kind: TrialVisitKindSchema.optional(),
    window: z.boolean().optional(),
    minDays: z.number().int().nullable().optional(),
    optDays: z.number().int().nullable().optional(),
    maxDays: z.number().int().nullable().optional(),
    referralVisitId: z.number().int().positive().nullable().optional(),
    // D2 (#40): null = detach the visit from its group ("bez grupy").
    groupId: z.number().int().positive().nullable().optional(),
    type: TrialVisitTypeSchema.optional(),
    contactType: TrialVisitContactSchema.optional(),
    referenceType: TrialVisitReferenceTypeSchema.optional(),
    referenceInstruction: z.string().max(10_000).nullable().optional(),
    description: z.string().max(10_000).nullable().optional(),
})
    .superRefine((val, ctx) => {
    for (const issue of visitWindowDayRangeIssues(val.minDays, val.optDays, val.maxDays)) {
        ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
    }
    if (val.window !== true)
        return;
    const missing = [];
    if (val.maxDays === null || val.maxDays === undefined)
        missing.push('maxDays');
    if (val.referralVisitId === null || val.referralVisitId === undefined)
        missing.push('referralVisitId');
    if (missing.length > 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['window'],
            message: windowedVisitRequiresMessage(missing),
        });
    }
});
// ---------------------------------------------------------------------------
// Visit window modes (D5, #36) — N:1 child of trial_visits
// ---------------------------------------------------------------------------
export const TrialVisitWindowModeDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    trialVisitId: z.number().int().positive(),
    label: z.string().min(1).max(255),
    isDefault: z.boolean(),
    // D2 (#36): signed per-mode day range; nullable = mode without a range.
    minDays: z.number().int().nullable(),
    optDays: z.number().int().nullable(),
    maxDays: z.number().int().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const CreateTrialVisitWindowModeRequestSchema = z
    .object({
    trialId: z.number().int().positive(),
    trialVisitId: z.number().int().positive(),
    label: z.string().min(1).max(255),
    isDefault: z.boolean().default(false),
    minDays: z.number().int().optional(),
    optDays: z.number().int().optional(),
    maxDays: z.number().int().optional(),
})
    .superRefine((val, ctx) => {
    // Optional range: absent (all undefined) = mode without a range, which is legal.
    if (val.minDays === undefined || val.optDays === undefined || val.maxDays === undefined)
        return;
    if (val.minDays > val.optDays) {
        ctx.addIssue({ code: 'custom', path: ['minDays'], message: 'minDays must be <= optDays' });
    }
    if (val.optDays > val.maxDays) {
        ctx.addIssue({ code: 'custom', path: ['optDays'], message: 'optDays must be <= maxDays' });
    }
});
export const UpdateTrialVisitWindowModeRequestSchema = z
    .object({
    label: z.string().min(1).max(255).optional(),
    isDefault: z.boolean().optional(),
    minDays: z.number().int().nullable().optional(),
    optDays: z.number().int().nullable().optional(),
    maxDays: z.number().int().nullable().optional(),
})
    .superRefine((val, ctx) => {
    // Missing (null/undefined) = no range; only validate when all three are provided.
    const { minDays, optDays, maxDays } = val;
    if (minDays == null || optDays == null || maxDays == null)
        return;
    if (minDays > optDays) {
        ctx.addIssue({ code: 'custom', path: ['minDays'], message: 'minDays must be <= optDays' });
    }
    if (optDays > maxDays) {
        ctx.addIssue({ code: 'custom', path: ['optDays'], message: 'optDays must be <= maxDays' });
    }
});
// ---------------------------------------------------------------------------
// Trial roles (REQ-020/021) — catalog locked in code, roles per trial
// ---------------------------------------------------------------------------
export const TrialRoleCodeSchema = z.enum(TRIAL_ROLE_CODES);
export const TrialRoleDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    roleCode: TrialRoleCodeSchema.nullable(),
    name: z.string().min(1).max(255),
    shortcut: z.string().min(1).max(16),
    siteStaff: z.boolean(),
    comments: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const CreateTrialRoleRequestSchema = z.object({
    trialId: z.number().int().positive(),
    roleCode: TrialRoleCodeSchema.optional(),
    name: z.string().min(1).max(255).optional(),
    shortcut: z.string().min(1).max(16).optional(),
    siteStaff: z.boolean().optional(),
    comments: z.string().max(10_000).optional(),
});
/**
 * REQ-020: manual roles may change name/shortcut/site staff; catalog roles are
 * LOCKED (only comments editable). roleCode is immutable and therefore absent.
 * The service enforces the catalog lock (it knows roleCode); the contract only
 * validates field shapes.
 */
export const UpdateTrialRoleRequestSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    shortcut: z.string().min(1).max(16).optional(),
    siteStaff: z.boolean().optional(),
    comments: z.string().max(10_000).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Trial personnel (REQ-016…018)
// ---------------------------------------------------------------------------
export const TrialPersonnelDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    roleId: z.number().int().positive(),
    personnelDetailsId: z.number().int().positive(),
    pi: z.boolean(),
    roleComments: z.string().nullable(),
    active: z.boolean(),
    dateStart: z.iso.date().nullable(),
    dateEnd: z.iso.date().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const CreateTrialPersonnelRequestSchema = z
    .object({
    trialId: z.number().int().positive(),
    roleId: z.number().int().positive(),
    personnelDetailsId: z.number().int().positive(),
    pi: z.boolean().default(false),
    roleComments: z.string().max(10_000).optional(),
    active: z.boolean().default(true),
    dateStart: z.iso.date().optional(),
    dateEnd: z.iso.date().optional(),
})
    .refine((val) => dateEndAfterStart(val), {
    path: ['dateEnd'],
    message: 'Data zakończenia nie może poprzedzać daty rozpoczęcia',
});
function dateEndAfterStart(val) {
    if (val.dateStart === undefined || val.dateEnd === undefined)
        return true;
    return val.dateEnd >= val.dateStart;
}
export const UpdateTrialPersonnelRequestSchema = z
    .object({
    roleId: z.number().int().positive().optional(),
    personnelDetailsId: z.number().int().positive().optional(),
    pi: z.boolean().optional(),
    roleComments: z.string().max(10_000).nullable().optional(),
    active: z.boolean().optional(),
    dateStart: z.iso.date().nullable().optional(),
    dateEnd: z.iso.date().nullable().optional(),
})
    .refine((val) => {
    if (val.dateStart === null || val.dateStart === undefined)
        return true;
    if (val.dateEnd === null || val.dateEnd === undefined)
        return true;
    return val.dateEnd >= val.dateStart;
}, { path: ['dateEnd'], message: 'Data zakończenia nie może poprzedzać daty rozpoczęcia' });
// ---------------------------------------------------------------------------
// Visit groups (D2, #40) — N:1 grouping of protocol catalog visits
// ---------------------------------------------------------------------------
export const TrialVisitGroupDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    name: z.string().min(1).max(255),
    createdAt: z.iso.datetime(),
});
export const CreateTrialVisitGroupRequestSchema = z.object({
    name: z.string().min(1).max(255),
});
/** Rename only — group membership lives on the visit side (`groupId`). */
export const UpdateTrialVisitGroupRequestSchema = z.object({
    name: z.string().min(1).max(255),
});
// ---------------------------------------------------------------------------
// Bulk creation of catalog visits (D2, #40) — the "++" wizard
// ---------------------------------------------------------------------------
/**
 * #40: protocols carry 100+ (even several hundred, #39) visits. The wizard
 * generates names client-side (common stem + counter: "Week 2..8 co 2",
 * prefix/suffix) with a live preview and posts the final list; the API
 * validates it and inserts in ONE transaction. `visitNumber` is ALWAYS
 * server-assigned (REQ-008: max+1 per trial, sequential within the bulk) —
 * items cannot set it. Items mirror CreateTrialVisitRequestSchema minus
 * `trialId`; `referralVisitId` must reference an EXISTING visit of the trial
 * (intra-bulk references are out of scope of v1).
 */
export const BULK_CREATE_TRIAL_VISITS_MAX_ITEMS = 500;
export const BulkCreateTrialVisitItemSchema = z
    .object({
    visitName1: z.string().min(1).max(255),
    visitName2: z.string().max(255).optional(),
    kind: TrialVisitKindSchema,
    window: z.boolean().default(false),
    minDays: z.number().int().optional(),
    optDays: z.number().int().optional(),
    maxDays: z.number().int().optional(),
    referralVisitId: z.number().int().positive().optional(),
    type: TrialVisitTypeSchema.default('S'),
    contactType: TrialVisitContactSchema.default('site'),
    referenceType: TrialVisitReferenceTypeSchema.default('visit'),
    referenceInstruction: z.string().max(10_000).optional(),
    description: z.string().max(10_000).optional(),
    // D2 (#40): "przy dodawaniu wielu — jednoczesne przypisanie do grup".
    groupId: z.number().int().positive().optional(),
})
    .superRefine((val, ctx) => {
    for (const issue of visitWindowDayRangeIssues(val.minDays, val.optDays, val.maxDays)) {
        ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
    }
    // REQ-009/010 mirror of CreateTrialVisitRequestSchema.
    if (!val.window)
        return;
    const missing = [];
    if (val.maxDays === undefined)
        missing.push('maxDays');
    if (val.referralVisitId === undefined)
        missing.push('referralVisitId');
    if (missing.length > 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['window'],
            message: windowedVisitRequiresMessage(missing),
        });
    }
});
export const BulkCreateTrialVisitsRequestSchema = z.object({
    items: z.array(BulkCreateTrialVisitItemSchema).min(1).max(BULK_CREATE_TRIAL_VISITS_MAX_ITEMS),
});
export const BulkCreateTrialVisitsResponseSchema = z.object({
    trialId: z.number().int().positive(),
    created: z.array(TrialVisitDtoSchema),
});
// ---------------------------------------------------------------------------
// #44 pkt 5 — bulk feature update ("Nadaj cechy zaznaczonym")
// ---------------------------------------------------------------------------
/**
 * Features are shared across the selection; names are intentionally absent —
 * the catalog keeps unique name pairs (#44 A1) and bulk renames have no
 * meaningful semantics. `referralPrevious` points every selected visit at its
 * catalog predecessor and is mutually exclusive with an explicit referral.
 */
export const BULK_UPDATE_TRIAL_VISITS_MAX_IDS = 500;
export const BulkUpdateTrialVisitsRequestSchema = z
    .object({
    ids: z
        .array(z.number().int().positive())
        .min(1)
        .max(BULK_UPDATE_TRIAL_VISITS_MAX_IDS),
    kind: TrialVisitKindSchema.optional(),
    type: TrialVisitTypeSchema.optional(),
    description: z.string().max(10_000).nullable().optional(),
    window: z.boolean().optional(),
    minDays: z.number().int().nullable().optional(),
    optDays: z.number().int().nullable().optional(),
    maxDays: z.number().int().nullable().optional(),
    referralVisitId: z.number().int().positive().nullable().optional(),
    referralPrevious: z.boolean().optional(),
    groupId: z.number().int().positive().nullable().optional(),
})
    .superRefine((val, ctx) => {
    for (const issue of visitWindowDayRangeIssues(val.minDays, val.optDays, val.maxDays)) {
        ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
    }
    if (val.referralPrevious === true && val.referralVisitId !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['referralPrevious'],
            message: 'Nie można podać jednocześnie odniesienia do poprzedniej wizyty i konkretnej wizyty odniesienia',
        });
    }
    // REQ-009/010 mirror of UpdateTrialVisitRequestSchema.
    if (val.window === true) {
        const missing = [];
        if (val.maxDays === null || val.maxDays === undefined)
            missing.push('maxDays');
        if (missing.length > 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['window'],
                message: windowedVisitRequiresMessage(missing),
            });
        }
    }
    const hasFeature = val.kind !== undefined ||
        val.type !== undefined ||
        val.description !== undefined ||
        val.window !== undefined ||
        val.minDays !== undefined ||
        val.optDays !== undefined ||
        val.maxDays !== undefined ||
        val.referralVisitId !== undefined ||
        val.referralPrevious !== undefined ||
        val.groupId !== undefined;
    if (!hasFeature) {
        ctx.addIssue({
            code: 'custom',
            path: ['ids'],
            message: 'Provide at least one feature to change',
        });
    }
});
export const BulkUpdateTrialVisitsResponseSchema = z.object({
    trialId: z.number().int().positive(),
    updated: z.array(TrialVisitDtoSchema),
});
// ---------------------------------------------------------------------------
// #44 pkt 6 — catalog reorder
// ---------------------------------------------------------------------------
export const ReorderTrialVisitRequestSchema = z.object({
    direction: z.enum(['up', 'down']),
});
/**
 * #44 pkt 6 (round 2): move a CONTIGUOUS block of selected visits up/down by
 * one position together (a „góra/dół" click on a selected visit moves the whole
 * adjacent selected run). The block must be contiguous in catalog order.
 */
export const ReorderTrialVisitsRequestSchema = z.object({
    ids: z.array(z.number().int().positive()).min(1).max(BULK_UPDATE_TRIAL_VISITS_MAX_IDS),
    direction: z.enum(['up', 'down']),
});
//# sourceMappingURL=trials.schema.js.map